import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { storage } from '@/lib/storage';
import { useAuthStore } from '@/store/authStore';
import { toast } from '@/store/toastStore';

const API_BASE = '/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  withCredentials: true,
});

/** 요청 인터셉터 — access token 주입 */
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = storage.get<string>('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** 401 → refresh token 재발급 시도 */
let isRefreshing = false;
let pendingRequests: Array<(token: string) => void> = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  pendingRequests.push(cb);
}
function onRefreshed(token: string) {
  pendingRequests.forEach((cb) => cb(token));
  pendingRequests = [];
}

/* 동일 에러 코드로 토스트가 연속 발생하는 것 방지 — 페이지 진입 시 동시 호출되는
   여러 query 가 모두 403 을 받으면 토스트가 N번 쌓이는 문제 회피. */
let lastAccountErrorAt = 0;

apiClient.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as InternalAxiosRequestConfig & { _retry?: boolean };

    /* 403 분기 — 계정 상태 이상 (비활성화/탈퇴) 시 강제 로그아웃 + 메인 이동.
       권한 부족(어드민 등) 일반 403 은 기존 처리(개별 페이지/컴포넌트) 그대로 위임. */
    if (err.response?.status === 403) {
      const code = (err.response.data as any)?.error?.code as string | undefined;
      if (code === 'ACCOUNT_INACTIVE' || code === 'ACCOUNT_DELETED') {
        const now = Date.now();
        if (now - lastAccountErrorAt > 3000) {
          lastAccountErrorAt = now;
          const message =
            code === 'ACCOUNT_INACTIVE'
              ? '장기 미접속으로 비활성화된 계정입니다. 고객센터(norigonggan@gmail.com)로 문의해주세요.'
              : '탈퇴한 계정입니다.';
          toast.error(message);
          useAuthStore.getState().logout();
          // 인터셉터는 react-router 의 navigate 에 접근 불가 — 하드 리다이렉트로 메인 이동.
          // replace 사용해 뒤로가기 히스토리에서 현재 화면을 제거.
          if (window.location.pathname !== '/') {
            window.location.replace('/');
          }
        }
        return Promise.reject(err);
      }
      // 그 외 403 은 기존 처리 위임
      return Promise.reject(err);
    }

    if (err.response?.status !== 401 || original._retry) {
      return Promise.reject(err);
    }

    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          resolve(apiClient(original));
        });
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${API_BASE}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      const newToken = data?.data?.accessToken as string | undefined;
      if (!newToken) throw new Error('refresh failed');

      storage.set('accessToken', newToken);
      onRefreshed(newToken);

      original.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(original);
    } catch (refreshErr) {
      useAuthStore.getState().logout();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  },
);

/** 표준 응답 Unwrapping */
export async function get<T>(
  url: string,
  params?: Record<string, unknown>,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.get(url, { params, ...config });
  return res.data?.data as T;
}
export async function post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.post(url, body, config);
  return res.data?.data as T;
}
export async function put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.put(url, body, config);
  return res.data?.data as T;
}
export async function patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.patch(url, body, config);
  return res.data?.data as T;
}
export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.delete(url, config);
  return res.data?.data as T;
}
